import { z } from 'zod'
import type { NotificationItem, NotificationKind } from '../model/types'

/*
 * Разбор `payload` уведомления. В контракте `payload` — `Record<string, unknown>`: имена полей
 * в схему не попадают, поэтому неизвестное сужается zod'ом, а не приведением. Два вида несут данные
 * для показа:
 *  - `legacy.message` — сообщение 1С как есть (`{ lines: string[], browser: string | null }`),
 *    показывается ТЕКСТОМ, без тегов и `innerHTML`;
 *  - `manager.invoice.changed` / `client.invoice.changed` — вид правки в `payload`; имя поля в
 *    контракте не названо (черновик, `BE-158`), поэтому читаем `view` мягко: нет — базовый текст.
 */

/** Виды правки счёта (§3.6 бэкенд-документа). `write-off` — дефис, как в перечне 1С. */
export const INVOICE_CHANGE_VIEWS = [
  'quantity',
  'remove',
  'comment',
  'email',
  'write-off',
  'confirm',
  'order',
] as const

export type InvoiceChangeView = (typeof INVOICE_CHANGE_VIEWS)[number]

/** Вид без текста в payload: `kind` + уточнение правки (если его удалось прочитать). */
type CodedKind = Exclude<NotificationKind, 'legacy.message'>

/**
 * Что показывать по уведомлению. `legacy` — готовые строки 1С (рендер текстом); `coded` — текст
 * выбирает интерфейс по `kind`, `detail` уточняет правку счёта там, где она есть.
 */
export type NotificationContent =
  | { variant: 'legacy'; lines: string[] }
  | { variant: 'coded'; kind: CodedKind; detail: InvoiceChangeView | null }

const legacyPayloadSchema = z.object({
  lines: z.array(z.string()),
  browser: z.string().nullable().optional(),
})

const invoiceChangePayloadSchema = z.object({
  view: z.enum(INVOICE_CHANGE_VIEWS),
})

/** Виды, у которых в payload бывает уточнение правки. */
const INVOICE_CHANGE_KINDS = new Set<NotificationKind>([
  'manager.invoice.changed',
  'client.invoice.changed',
])

export function describeNotification(item: NotificationItem): NotificationContent {
  if (item.kind === 'legacy.message') {
    const parsed = legacyPayloadSchema.safeParse(item.payload)
    // Непарсящийся payload — показывать нечего; пустые строки отбрасываем, текст 1С не трогаем
    const lines = parsed.success ? parsed.data.lines.filter((line) => line.trim() !== '') : []
    return { variant: 'legacy', lines }
  }

  const detail = INVOICE_CHANGE_KINDS.has(item.kind)
    ? (invoiceChangePayloadSchema.safeParse(item.payload).data?.view ?? null)
    : null

  return { variant: 'coded', kind: item.kind, detail }
}
